import React, { useRef, useCallback, useEffect, useState } from 'react';
// @ts-ignore
import { ReactPhotoSphereViewer } from 'react-photo-sphere-viewer';
import type { PanoramaState } from './types';

interface Props {
  src: string;
  panoramaState: PanoramaState | null;
  onPositionChange: (yaw: number, pitch: number) => void;
  onZoomChange: (zoom: number) => void;
  onReady: () => void;
  onError: () => void;
}

/** Pre-validate an image URL using a hidden Image element. */
function preloadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject();
    img.src = url;
  });
}

const DEFAULT_ZOOM = 50;

const PanoramicImageViewer: React.FC<Props> = ({
  src,
  panoramaState,
  onPositionChange,
  onZoomChange,
  onReady,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<any>(null);
  const readyFired = useRef(false);
  const loadedSrcRef = useRef<string | null>(null);
  const resizeTimeoutId = useRef<number | null>(null);
  const initialRafId = useRef<number | null>(null);
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onPositionChangeRef = useRef(onPositionChange);
  const onZoomChangeRef = useRef(onZoomChange);
  const panoramaStateRef = useRef(panoramaState);
  const forceInitialResizeRef = useRef<(api: any) => void>(() => {});

  // Capture the initial source/camera to initialize the viewer.
  // Live yaw/pitch/zoom must NOT be passed as default* props — react-photo-sphere-viewer
  // freezes the first onPositionChange/onZoomChange closures, and changing defaults
  // also churns its options memo. Subsequent updates go through setPanorama/rotate/zoom.
  const [initialSrc] = useState(src);
  const [initialYaw] = useState(() => panoramaState?.yaw ?? 0);
  const [initialPitch] = useState(() => panoramaState?.pitch ?? 0);
  const [initialZoom] = useState(() => panoramaState?.zoom ?? DEFAULT_ZOOM);
  // null = checking, true = loadable, false = broken
  const [imageValid, setImageValid] = useState<boolean | null>(null);

  const resizeViewerToContainer = useCallback((api: any) => {
    const el = containerRef.current;
    if (!api || !el) return false;
    const rect = el.getBoundingClientRect();
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);
    if (w < 2 || h < 2) return false;

    try {
      if (typeof api.resize === 'function') {
        api.resize({ width: `${w}px`, height: `${h}px` });
      } else if (typeof api.autoSize === 'function') {
        api.autoSize();
      }
    } catch {
      // ignore
    }
    return true;
  }, []);

  const scheduleDebouncedResize = useCallback(() => {
    if (!readyFired.current) return;
    const api = viewerRef.current;
    if (!api) return;

    if (resizeTimeoutId.current !== null) {
      window.clearTimeout(resizeTimeoutId.current);
    }

    // Debounce so we don't spam resize during drag-resize (prevents flicker).
    resizeTimeoutId.current = window.setTimeout(() => {
      resizeTimeoutId.current = null;
      resizeViewerToContainer(api);
    }, 120);
  }, [resizeViewerToContainer]);

  const forceInitialResize = useCallback((api: any) => {
    // PSV can become ready before the modal has finished its first layout.
    // Try for a few frames until the container has a real size.
    if (initialRafId.current !== null) {
      window.cancelAnimationFrame(initialRafId.current);
      initialRafId.current = null;
    }

    let attempts = 0;
    const tick = () => {
      initialRafId.current = null;
      const ok = resizeViewerToContainer(api);
      if (!ok && attempts++ < 8) {
        initialRafId.current = window.requestAnimationFrame(tick);
      }
    };

    initialRafId.current = window.requestAnimationFrame(tick);
  }, [resizeViewerToContainer]);

  const applyPanoramaState = useCallback((api: any, state: PanoramaState | null | undefined) => {
    if (!api || !state) return;
    try {
      if (Number.isFinite(state.yaw) && Number.isFinite(state.pitch) && typeof api.rotate === 'function') {
        api.rotate({ yaw: state.yaw, pitch: state.pitch });
      }
      if (Number.isFinite(state.zoom) && typeof api.zoom === 'function') {
        api.zoom(state.zoom);
      }
    } catch {
      // ignore if not ready
    }
  }, []);

  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
    onPositionChangeRef.current = onPositionChange;
    onZoomChangeRef.current = onZoomChange;
    panoramaStateRef.current = panoramaState;
    forceInitialResizeRef.current = forceInitialResize;
  }, [onReady, onError, onPositionChange, onZoomChange, panoramaState, forceInitialResize]);

  // Observe container resizes (modal resize mutates element dimensions directly).
  useEffect(() => {
    if (imageValid !== true) return;
    const el = containerRef.current;
    if (!el) return;

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        scheduleDebouncedResize();
      });
      observer.observe(el);
      return () => observer.disconnect();
    }

    const onWinResize = () => scheduleDebouncedResize();
    window.addEventListener('resize', onWinResize);
    return () => window.removeEventListener('resize', onWinResize);
  }, [imageValid, scheduleDebouncedResize]);

  // Cleanup timers/raf.
  useEffect(() => {
    return () => {
      if (resizeTimeoutId.current !== null) {
        window.clearTimeout(resizeTimeoutId.current);
        resizeTimeoutId.current = null;
      }
      if (initialRafId.current !== null) {
        window.cancelAnimationFrame(initialRafId.current);
        initialRafId.current = null;
      }
    };
  }, []);

  // Pre-validate initial src (and whenever src changes before viewer is mounted)
  useEffect(() => {
    // If the viewer is already mounted we handle src changes via setPanorama below
    if (readyFired.current) return;

    let cancelled = false;
    setImageValid(null);
    preloadImage(src)
      .then(() => { if (!cancelled) setImageValid(true); })
      .catch(() => { if (!cancelled) { setImageValid(false); onError(); } });
    return () => { cancelled = true; };
  }, [src]); // intentionally only depend on src

  const handleReady = useCallback(
    (instance: any) => {
      readyFired.current = true;
      loadedSrcRef.current = src;
      applyPanoramaState(instance, panoramaStateRef.current);
      // Fix initial black canvas by forcing a resize after layout stabilizes.
      forceInitialResizeRef.current(instance);
      onReadyRef.current();
    },
    [applyPanoramaState, src],
  );

  // Stable handlers: react-photo-sphere-viewer only wires these once at mount.
  const handlePositionChange = useCallback(
    (lat: number, lng: number) => {
      onPositionChangeRef.current(lng, lat); // PSV passes pitch,yaw as lat,lng
    },
    [],
  );

  const handleZoomChange = useCallback(
    (data: { zoomLevel?: number } | number | null | undefined) => {
      const zoomLevel =
        typeof data === 'number'
          ? data
          : (data?.zoomLevel ?? DEFAULT_ZOOM);
      onZoomChangeRef.current(zoomLevel);
    },
    [],
  );

  // When src changes on an existing viewer, pre-validate then use setPanorama
  // to swap the image while retaining camera position
  useEffect(() => {
    if (viewerRef.current && readyFired.current) {
      const psvInstance = viewerRef.current;
      if (loadedSrcRef.current === src) return;

      loadedSrcRef.current = src;
      let cancelled = false;
      preloadImage(src)
        .then(() => {
          if (cancelled || !psvInstance.setPanorama) return;
          return psvInstance
            .setPanorama(src, { transition: false, showLoader: false })
            .then(() => {
              // setPanorama can reset camera; restore retained yaw/pitch/zoom.
              applyPanoramaState(psvInstance, panoramaStateRef.current);
              forceInitialResizeRef.current(psvInstance);
              onReadyRef.current();
            })
            .catch(() => onErrorRef.current());
        })
        .catch(() => { if (!cancelled) onErrorRef.current(); });
      return () => { cancelled = true; };
    }
  }, [applyPanoramaState, src]);

  // Don't mount the 3D viewer until we know the image is loadable
  if (imageValid !== true) return null;

  return (
    <div ref={containerRef} className="panoramic-image-container">
      <ReactPhotoSphereViewer
        ref={viewerRef}
        src={initialSrc}
        height="100%"
        width="100%"
        navbar={false}
        defaultYaw={initialYaw}
        defaultPitch={initialPitch}
        defaultZoomLvl={initialZoom}
        onReady={handleReady}
        onPositionChange={handlePositionChange}
        onZoomChange={handleZoomChange}
      />
    </div>
  );
};

export default PanoramicImageViewer;
